import { taggedShareUrl } from '../lib/trafficSource';
import React, { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { FaWhatsapp, FaFacebookF, FaTelegramPlane } from 'react-icons/fa';
import { BiX, BiLink, BiCheck, BiShareAlt, BiVolumeFull, BiVolumeMute } from 'react-icons/bi';
import { shareSoundEnabled, setShareSoundEnabled } from '../lib/shareSound';

export default function ShareDialog({ url, postId, onClose, user }) {
  const canTrack = user?.email === 'warigiachegesa@gmail.com';
  const dialog = useRef(null);
  const firstOption = useRef(null);
  const input = useRef(null);
  const [message, setMessage] = useState('');
  const [copied, setCopied] = useState(false);
  const [trackingLink, setTrackingLink] = useState('');
  const trackingInput = useRef(null);
  const [sound, setSound] = useState(shareSoundEnabled);
  useEffect(() => {
    const previousFocus = document.activeElement;
    const previousOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    if (dialog.current?.showModal && !dialog.current.open) dialog.current.showModal();
    firstOption.current?.focus();
    return () => {
      document.body.style.overflow = previousOverflow;
      if (previousFocus?.isConnected) previousFocus.focus();
    };
  }, []);
  const encodedFor = source => encodeURIComponent(taggedShareUrl(url, source));
  const text = encodeURIComponent('Share a laugh with HappyTimes');
  const copy = async () => {
    try {
      await navigator.clipboard.writeText(url);
      setCopied(true); setMessage('Link copied. Send it to someone who needs a laugh.');
    } catch {
      input.current?.focus(); input.current?.select();
      setMessage('Select and copy this link to share.');
    }
  };
  const copyTracked = async (source, label) => {
    if (!canTrack) return;
    const link = taggedShareUrl(url, source);
    setTrackingLink(link);
    try {
      await navigator.clipboard.writeText(link);
      setMessage(`${label} tracking link copied.`);
    } catch {
      setMessage('Select and copy the tracking link below.');
      requestAnimationFrame(() => { trackingInput.current?.focus(); trackingInput.current?.select(); });
    }
  };
  const more = async () => {
    try { await navigator.share({ title: 'HappyTimes', text: 'Share a laugh with HappyTimes', url }); }
    catch (error) { if (error.name !== 'AbortError') setMessage('Choose an app or copy the link.'); }
  };
  return createPortal(<dialog ref={dialog} id={`share-${postId}`} className="share-dialog" aria-labelledby={`share-title-${postId}`}
    onCancel={event => { event.preventDefault(); onClose(); }}
    onClick={event => { if (event.target === dialog.current) { const rect = dialog.current.getBoundingClientRect(); if (event.clientX < rect.left || event.clientX > rect.right || event.clientY < rect.top || event.clientY > rect.bottom) onClose(); } }}>
    <header className="share-dialog-heading"><div><h2 id={`share-title-${postId}`}>Pass on the laugh.</h2><p>Better when it’s shared.</p></div><button className="share-dialog-close" aria-label="Close share menu" onClick={onClose}><BiX size={24} /></button></header>
    <div className="share-destinations">
      <a ref={firstOption} href={`https://wa.me/?text=${encodeURIComponent(`Share a laugh with HappyTimes ${taggedShareUrl(url, 'whatsapp')}`)}`} target="_blank" rel="noopener noreferrer"><span className="share-app-icon share-whatsapp"><FaWhatsapp /></span>WhatsApp</a>
      <a href={`https://www.facebook.com/sharer/sharer.php?u=${encodedFor('facebook')}`} target="_blank" rel="noopener noreferrer"><span className="share-app-icon share-facebook"><FaFacebookF /></span>Facebook</a>
      <a href={`https://t.me/share/url?url=${encodedFor('telegram')}&text=${text}`} target="_blank" rel="noopener noreferrer"><span className="share-app-icon share-telegram"><FaTelegramPlane /></span>Telegram</a>
      <a href={`https://twitter.com/intent/tweet?url=${encodedFor('x')}&text=${text}`} target="_blank" rel="noopener noreferrer"><span className="share-app-icon share-x" aria-hidden="true">𝕏</span>X</a>
    </div>
    <div className="share-copy-row"><BiLink aria-hidden="true" /><input ref={input} aria-label="Meme share link" readOnly value={url} onFocus={event => event.target.select()} /><button onClick={copy} aria-label={copied ? 'Link copied' : 'Copy link'}>{copied ? <><BiCheck />Copied</> : 'Copy link'}</button></div>
    {canTrack && <div className="share-tracking-options" aria-label="WhatsApp tracking links">
      <button onClick={() => copyTracked('whatsapp_stories', 'WhatsApp Stories')}>Copy WhatsApp Stories link</button>
      <button onClick={() => copyTracked('whatsapp_channel', 'WhatsApp Channel')}>Copy WhatsApp Channel link</button>
    </div>}
    {canTrack && trackingLink && <div className="share-copy-row"><BiLink aria-hidden="true" /><input ref={trackingInput} aria-label="WhatsApp tracking link" readOnly value={trackingLink} onFocus={event => event.target.select()} /></div>}
    <div className="share-dialog-feedback" role="status">{message}</div>
    <footer className="share-dialog-footer">{typeof navigator.share === 'function' ? <button className="share-more-apps" onClick={more}><BiShareAlt />More apps…</button> : <span>HappyTimes</span>}<button className="share-sound-toggle" aria-label="Share sound" aria-pressed={sound} title={sound ? 'Turn share sound off' : 'Turn share sound on'} onClick={() => { setSound(!sound); setShareSoundEnabled(!sound); }}>{sound ? <BiVolumeFull /> : <BiVolumeMute />}</button></footer>
  </dialog>, document.body);
}
